import { BodyText, DetailText, HeadlineText, JumboText, LeftArrowIcon } from 'design-configuration/components'
import React from 'react'
import { StackedImages } from '../[slug]/components/stacked-images'
import { getPrefixedLinkPath } from '@components/prefixed-link'
import { ThreeImageRow } from '../[slug]/components/three-image-row'
import { exhibitionTransition } from '../[slug]/components/header'
import { motion } from 'motion/react'
import { AllPurposeLink } from '@components/all-purpose-link'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { useSoloViewer } from '@components/solo-viewer/provider'
import { articleRoute } from '@utils/routes'
import { TrackedButtonNames, trackButtonClicked } from '@utils/analytics'

export const BehindTheExhibition = (): React.ReactElement => {
  const { setSoloViewerImageData } = useSoloViewer()
  return (
    <div className="w-full">
      <JumboText className="px-md md:pb-xl pb-md font-medium">Behind the exhibition</JumboText>
      {/* image */}
      <div className="mb-[80px] flex w-full flex-col gap-y-[80px]">
        <div>
          <motion.button
            className="relative aspect-[16/9] w-full md:w-[80vw]"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={exhibitionTransition}
            viewport={{ once: true, amount: 0.1 }}
            onClick={() => {
              setSoloViewerImageData({
                image: getPrefixedLinkPath('/images/poetics/behindTheExhibition/behindExhibition-1.jpg'),
                imageList: [getPrefixedLinkPath('/images/poetics/behindTheExhibition/behindExhibition-1.jpg')]
              })
            }}
          >
            <ImageWithCustomLoader
              src={getPrefixedLinkPath('/images/poetics/behindTheExhibition/behindExhibition-1.jpg')}
              alt="Behind the exhibition top image"
              style={{
                objectFit: 'cover'
              }}
              fill
            />
          </motion.button>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={exhibitionTransition}
            viewport={{ once: true, amount: 0.3 }}
          >
            {
              <DetailText className="px-md pt-xs text-on-surface-variant">
                A glimpse into the creative space where Lieberman produces and practices his artistic work.
              </DetailText>
            }
          </motion.div>
        </div>
        {/* blurb */}
        <div className="px-md lg:px-2xl flex w-full justify-end">
          <motion.div
            className="max-w-[750px]"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={exhibitionTransition}
            viewport={{ once: true, amount: 0.3 }}
          >
            <HeadlineText className="mb-md font-light">Exploring the boundaries of perception</HeadlineText>
            <BodyText>
              Fundamental to <span className="italic">Poetics of the Infinite</span> is a commitment to pushing the
              boundaries of generative art. Each artist engages in a unique process that merges intention with
              spontaneity, allowing their works to emerge moment by moment across both physical and digital realms. This
              exhibition highlights the transient nature of art-making, where each step reflects the artist&apos;s
              journey through an active engagement with the unrepresentable.
            </BodyText>
          </motion.div>
        </div>
        {/* three image row */}
        <ThreeImageRow
          items={[
            {
              imageUrl: getPrefixedLinkPath('/images/poetics/behindTheExhibition/behindExhibition-2.jpg'),
              caption: 'Velitchkova in her studio.'
            },
            {
              imageUrl: getPrefixedLinkPath('/images/poetics/behindTheExhibition/behindExhibition-3.jpg'),
              caption: "Wander's studio, featuring a plotter that uses watercolor paints."
            },
            {
              imageUrl: getPrefixedLinkPath('/images/poetics/behindTheExhibition/behindExhibition-4.jpg'),
              caption: 'Lieberman reviewing prints from his practice in his studio.'
            }
          ]}
        />
        {/* 2nd blurb */}
        <div className="px-md lg:px-2xl flex w-full justify-end">
          <motion.div
            className="max-w-[750px]"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={exhibitionTransition}
            viewport={{ once: true, amount: 0.3 }}
          >
            <BodyText>
              Through these collections, the artworks invite viewers to contemplate the infinite across a multitude of
              artistic practices. For Iskra Velitchkova, the elusive study of memory pulses through each recursive line,
              configured through endless variations. Her algorithmic impressions and personal experiences are translated
              to formulas for the delicate medium of fragrance—which evolves and decays as perception itself
              does—generating compositions deeply intertwined with their algorithmic manifestations. In this exploration
              of endless configurations, Lars Wander&apos;s process unfolds through algorithmic inquiry, with his
              interest in mathematical set theory informing his practice and guiding his exploration of form.
              Separately, Zach Lieberman&apos;s artistic practice explores the realms of vision, light, and rendered
              constraints, capturing the viewer&apos;s perception across and through the screen, extending it into the
              tangible world. Practicing his craft within his studio—where both manufactured neon lights and the flow of
              daylight serve as constant inspiration—he navigates the dynamic interplay between the apparent and the
              coming-to-be.
            </BodyText>
          </motion.div>
        </div>
        {/* stacked images */}
        <StackedImages
          topImage={getPrefixedLinkPath('/images/poetics/behindTheExhibition/behindExhibition-5.jpg')}
          bottomImageRow={[
            getPrefixedLinkPath('/images/poetics/behindTheExhibition/behindExhibition-6.jpg'),
            getPrefixedLinkPath('/images/poetics/behindTheExhibition/behindExhibition-7.jpg'),
            getPrefixedLinkPath('/images/poetics/behindTheExhibition/behindExhibition-8.jpg')
          ]}
          caption="From top to bottom, left to right: Ink pools on Velitchkova's artwork; Wander's watercolor pans set aside the plotter; a view of the fragrance crafting bench; Lieberman's desk, strewn."
        />
        {/* 3rd blurb and learn more */}
        <div className="px-md lg:px-2xl flex w-full justify-end">
          <motion.div
            className="max-w-[750px]"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={exhibitionTransition}
            viewport={{ once: true, amount: 0.3 }}
          >
            <BodyText>
              Distinct yet interconnected, Poetics of the Infinite unites three unique artists with practices that
              prompt viewers to consider what can be and what may emerge. The physicalities of the world are reshaped
              through perception and experience, rendering an endless realm of interpretation that finds resonance in
              concepts that are both familiar and previously uncharted. Each artist—Velitchkova, Wander, and
              Lieberman—invites audiences into their respective realms of exploration, challenging them to engage with
              the fluidity of perception and the limitless possibilities that arise from the act of creation. In this
              space, viewers are encouraged to embark on their own journeys, discovering new dimensions of meaning and
              experience.
            </BodyText>
            <AllPurposeLink
              href={articleRoute('breaking-the-box-of-perception-three-artists-confront-the-face-of-the-infinite')}
              className="mt-md gap-x-xs flex items-center"
              onClick={() => {
                trackButtonClicked(TrackedButtonNames.exhibitionBehindTheExhibitionLearnMore, {
                  customProperties: {
                    'Article name': 'Breaking the box of perception: three artists confront the face of the infinite'
                  }
                })
              }}
            >
              <BodyText>Learn more</BodyText>
              <LeftArrowIcon className="rotate-[180deg]" />
            </AllPurposeLink>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
